import React from "react";
import styled from "styled-components";
import { theme } from "@src/theme";
import Spinner from "@src/components/Spinner";
import { ICuratorConversation } from "@src/types/curator";
import CuratorBadge from "@src/assets/img/curator-badge.svg";
import {
  getConversationsForTab,
  getClientDisplayName,
  getClientAvatarUrl,
  getConversationId,
  getConversationPreview,
  getConversationStatus,
  getConversationTimestamp,
  formatConversationListTime,
  getConversationUnreadCount,
  isPeerCurator,
  type MessagesTab,
} from "./conversationUtils";
import {
  FIGMA_SELECTED_ROW,
  FIGMA_SIDEBAR_BG,
  FIGMA_BLOCKED_SIDEBAR_BG,
  FIGMA_BLOCKED_ROW_SELECTED,
  FIGMA_BLOCKED_ROW_HOVER,
  FIGMA_SIDEBAR_WIDTH,
  FIGMA_AVATAR_SIZE,
} from "./MessagesPage.styles";
import { SidebarEmptyState } from "./MessagesEmptyState";

const SidebarContainer = styled.div<{ $isBlockedView?: boolean }>`
  width: ${FIGMA_SIDEBAR_WIDTH};
  background-color: ${(props) =>
    props.$isBlockedView ? FIGMA_BLOCKED_SIDEBAR_BG : FIGMA_SIDEBAR_BG};
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${theme.colors.darkGray};
  flex-shrink: 0;
  min-height: 0;
`;

const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 9px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(155, 155, 155, 0.43);
    border-radius: 999px;
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.43) transparent;
`;

const ConversationItem = styled.div<{
  $selected: boolean;
  $isBlockedView: boolean;
  $isEndedView: boolean;
}>`
  display: flex;
  align-items: flex-start;
  padding: 14px 16px;
  cursor: pointer;
  background-color: ${(props) => {
    if (!props.$selected) return "transparent";
    if (props.$isBlockedView) return FIGMA_BLOCKED_ROW_SELECTED;
    if (props.$isEndedView) return "rgba(206, 206, 206, 0.1)";
    return FIGMA_SELECTED_ROW;
  }};
  box-shadow: ${(props) => {
    if (!props.$selected || props.$isBlockedView) return "none";
    if (props.$isEndedView) return "inset 3px 0 0 #979794";
    return `inset 3px 0 0 ${theme.colors.yellow}`;
  }};
  &:hover {
    background-color: ${(props) => {
      if (props.$isBlockedView) {
        return props.$selected
          ? FIGMA_BLOCKED_ROW_HOVER
          : "rgba(170, 9, 9, 0.12)";
      }
      if (props.$isEndedView) return "rgba(206, 206, 206, 0.1)";
      return FIGMA_SELECTED_ROW;
    }};
  }
  border-bottom: 1px solid ${theme.colors.darkGray};
`;

const AvatarWrapper = styled.div`
  position: relative;
  margin-right: 14px;
  flex-shrink: 0;
`;

const Avatar = styled.div<{ $muted?: boolean; $blocked?: boolean }>`
  width: ${(props) => (props.$blocked ? FIGMA_AVATAR_SIZE : "44px")};
  height: ${(props) => (props.$blocked ? FIGMA_AVATAR_SIZE : "44px")};
  border-radius: 50%;
  background-color: ${(props) =>
    props.$muted ? theme.colors.gray200 : theme.colors.yellow};
  color: ${(props) => (props.$muted ? theme.colors.white : theme.colors.black)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: ${(props) => (props.$blocked ? "1.35rem" : "1.2rem")};
  overflow: hidden;
  border: ${(props) =>
    props.$blocked ? `0.87px solid ${theme.colors.yellow}` : "none"};
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const getInitials = (name: string) => {
  if (!name) return "?";
  return name.charAt(0).toUpperCase();
};

const AvatarContent: React.FC<{ avatarUrl: string; name: string }> = ({
  avatarUrl,
  name,
}) => {
  const [failed, setFailed] = React.useState(false);

  if (avatarUrl && !failed) {
    return (
      <AvatarImage src={avatarUrl} alt={name} onError={() => setFailed(true)} />
    );
  }

  return <>{getInitials(name)}</>;
};

const CuratorBadgeImg = styled.img`
  position: absolute;
  top: 3px;
  right: -2px;
  width: 17px;
  height: 17px;
`;

const ItemContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  min-width: 0;
  gap: 4px;
`;

const TopLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.margins.small};
`;

const Name = styled.span<{
  $isPeerCurator: boolean;
  $muted?: boolean;
  $isBlockedView?: boolean;
}>`
  color: ${(props) => {
    if (props.$muted) return "#979794";
    if (props.$isBlockedView) return theme.colors.yellow;
    return props.$isPeerCurator ? theme.colors.yellow : theme.colors.gray;
  }};
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Time = styled.span<{ $muted?: boolean }>`
  color: #cecece;
  font-size: 10px;
  line-height: 19px;
  letter-spacing: -0.02em;
  flex-shrink: 0;
  text-align: right;
`;

const BottomLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.margins.small};
`;

const MessagePreview = styled.span<{
  $muted?: boolean;
  $selected?: boolean;
  $blocked?: boolean;
}>`
  color: ${(props) => {
    if (props.$blocked) return "#cecece";
    if (props.$muted) {
      return props.$selected ? "#cecece" : "#979794";
    }
    return "#cecece";
  }};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-style: ${(props) => (props.$muted ? "italic" : "normal")};
  flex: 1;
  min-width: 0;
`;

const RightMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
`;

const CheckCircleBadge = styled.span`
  width: 19px;
  height: 19px;
  border-radius: 50%;
  background-color: ${theme.colors.yellow};
  color: ${theme.colors.black};
  font-size: 10px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: -0.02em;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UnreadBadge = styled.span`
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background-color: ${theme.colors.yellow};
  color: ${theme.colors.black};
  font-size: 0.65rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyListState = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
`;

interface ConversationSidebarProps {
  activeTab: MessagesTab;
  selectedConversationId: string | null;
  onSelectConversation: (id: string) => void;
  conversations: ICuratorConversation[];
  isLoading: boolean;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  activeTab,
  selectedConversationId,
  onSelectConversation,
  conversations,
  isLoading,
}) => {
  const filteredConversations = getConversationsForTab(
    conversations,
    activeTab
  );
  const isBlockedView = activeTab === "blocked" || activeTab === "ai_blocked";
  const isEndedView = activeTab === "ended";

  return (
    <SidebarContainer $isBlockedView={isBlockedView}>
      <ListContainer>
        {isLoading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <Spinner />
          </div>
        )}
        {!isLoading && filteredConversations.length === 0 && (
          <EmptyListState>
            <SidebarEmptyState activeTab={activeTab} />
          </EmptyListState>
        )}
        {filteredConversations.map((conversation) => {
          const conversationId = getConversationId(conversation);
          const clientName = getClientDisplayName(conversation);
          const status = getConversationStatus(conversation);
          const isEnded = status === "ended";
          const preview = getConversationPreview(conversation, activeTab);
          const unreadCount = getConversationUnreadCount(conversation);
          const isSelected = selectedConversationId === conversationId;
          const avatarUrl = getClientAvatarUrl(conversation);
          const showCuratorBadge = isPeerCurator(conversation);
          const timestamp = formatConversationListTime(
            getConversationTimestamp(conversation)
          );

          return (
            <ConversationItem
              key={conversationId}
              $selected={isSelected}
              $isBlockedView={isBlockedView}
              $isEndedView={isEndedView}
              onClick={() => onSelectConversation(conversationId)}
            >
              <AvatarWrapper>
                <Avatar $muted={isEndedView} $blocked={isBlockedView}>
                  <AvatarContent avatarUrl={avatarUrl} name={clientName} />
                </Avatar>
                {showCuratorBadge && (
                  <CuratorBadgeImg src={CuratorBadge} alt="Curator" />
                )}
              </AvatarWrapper>
              <ItemContent>
                <TopLine>
                  <Name
                    $isPeerCurator={showCuratorBadge}
                    $muted={isEndedView}
                    $isBlockedView={isBlockedView}
                  >
                    {clientName}
                  </Name>
                  <RightMeta>
                    <Time $muted={isEndedView}>{timestamp}</Time>
                    {isBlockedView && unreadCount > 0 && (
                      <CheckCircleBadge>
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </CheckCircleBadge>
                    )}
                  </RightMeta>
                </TopLine>
                <BottomLine>
                  <MessagePreview
                    $muted={isEnded}
                    $selected={isSelected}
                    $blocked={isBlockedView}
                  >
                    {preview}
                  </MessagePreview>
                  {!isBlockedView && unreadCount > 0 && (
                    <UnreadBadge>
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </UnreadBadge>
                  )}
                </BottomLine>
              </ItemContent>
            </ConversationItem>
          );
        })}
      </ListContainer>
    </SidebarContainer>
  );
};

export default ConversationSidebar;
